import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";

const CONSENT_STORAGE_KEY = "mib_cookie_consent_v1";

function readStoredConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveConsent(payload) {
  if (typeof window === "undefined") return;
  const consent = {
    necessary: true,
    analytics: Boolean(payload.analytics),
    marketing: Boolean(payload.marketing),
    saved_at: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    window.dispatchEvent(new CustomEvent("mib:cookie-consent-updated", { detail: consent }));
  } catch {
    // Ignore storage errors; the banner is non-critical UI.
  }
}

export default function CookieConsentBanner() {
  const location = useLocation();
  const [practiceHome, setPracticeHome] = useState(window.__mibPracticeHome);
  const [visible, setVisible] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [choices, setChoices] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const update = () => setPracticeHome(window.__mibPracticeHome);
    window.addEventListener('mib:home-context-resolved', update);
    update();
    return () => window.removeEventListener('mib:home-context-resolved', update);
  }, []);

  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored) {
      setVisible(true);
      return;
    }
    setChoices({
      analytics: Boolean(stored.analytics),
      marketing: Boolean(stored.marketing),
    });
  }, []);

  const accept = (nextChoices) => {
    saveConsent(nextChoices);
    setChoices(nextChoices);
    setVisible(false);
  };

  // Public practice pages have their own tenant-scoped consent. Never borrow
  // the application's consent or show a second banner over their forms.
  if (location.pathname === "/angebot-annehmen" || !visible || /^\/(book|studio)\//.test(location.pathname)
    || (location.pathname === '/' && practiceHome !== false)) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-4 sm:px-6 sm:pb-6">
      <div className="mx-auto max-w-3xl rounded-2xl border border-[#E5E5EA] bg-white/95 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl sm:p-5">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#F5F5F7] text-[#1D1D1F]">
            <Cookie className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[#1D1D1F]">Cookie-Einstellungen</p>
                <p className="mt-1 text-sm leading-relaxed text-[#6B7280]">
                  Wir verwenden notwendige Cookies für Login, Sicherheit und Buchungsfunktionen. Optionale Cookies helfen uns, die Anwendung zu verbessern.
                </p>
              </div>
              <button
                type="button"
                onClick={() => accept({ analytics: false, marketing: false })}
                className="rounded-full p-1 text-[#86868B] transition hover:bg-[#F5F5F7] hover:text-[#1D1D1F]"
                aria-label="Cookie-Banner schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {detailsOpen ? (
              <div className="mt-4 grid gap-3 rounded-2xl bg-[#F5F5F7] p-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-[#1D1D1F]">Notwendig</p>
                    <p className="text-xs text-[#86868B]">Erforderlich für Authentifizierung und Grundfunktionen.</p>
                  </div>
                  <span className="text-xs font-medium text-[#86868B]">Immer aktiv</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-[#1D1D1F]">Analyse</p>
                    <p className="text-xs text-[#86868B]">Hilft uns, Nutzung und Stabilität besser zu verstehen.</p>
                  </div>
                  <Switch
                    checked={choices.analytics}
                    onCheckedChange={(checked) => setChoices((current) => ({ ...current, analytics: Boolean(checked) }))}
                    aria-label="Analyse-Cookies erlauben"
                  />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-[#1D1D1F]">Marketing</p>
                    <p className="text-xs text-[#86868B]">Ermöglicht relevantere Kampagnen und Messung.</p>
                  </div>
                  <Switch
                    checked={choices.marketing}
                    onCheckedChange={(checked) => setChoices((current) => ({ ...current, marketing: Boolean(checked) }))}
                    aria-label="Marketing-Cookies erlauben"
                  />
                </div>
              </div>
            ) : null}

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <Link to="/datenschutz" className="text-sm font-medium text-[#0071E3] hover:underline">
                Datenschutz ansehen
              </Link>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  className="btn-secondary"
                  onClick={() => {
                    if (detailsOpen) {
                      accept(choices);
                      return;
                    }
                    setDetailsOpen(true);
                  }}
                >
                  {detailsOpen ? "Auswahl speichern" : "Anpassen"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="btn-secondary"
                  onClick={() => accept({ analytics: false, marketing: false })}
                >
                  Nur notwendige
                </Button>
                <Button
                  type="button"
                  className="btn-primary"
                  onClick={() => accept({ analytics: true, marketing: true })}
                >
                  Alle akzeptieren
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
